import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { UserCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { subDays, format, startOfWeek, endOfWeek } from 'date-fns';

interface UserRetentionChartProps {
  companyId?: string | null;
}

export function UserRetentionChart({ companyId }: UserRetentionChartProps) {
  const { data: retentionData = [], isLoading } = useQuery({
    queryKey: ['user-retention', companyId],
    queryFn: async () => {
      // Get users who had activity in each of the last 4 weeks
      const weeks = [];
      const today = new Date();

      for (let i = 3; i >= 0; i--) {
        const weekStart = startOfWeek(subDays(today, i * 7));
        const weekEnd = endOfWeek(subDays(today, i * 7));
        
        let query = supabase
          .from('simulation_evaluations')
          .select('user_id, created_at')
          .gte('created_at', weekStart.toISOString())
          .lte('created_at', weekEnd.toISOString())
          .eq('is_submitted', true)
          .is('deleted_at', null);

        if (companyId) {
          query = query.eq('company_id', companyId);
        }

        const { data } = await query;
        
        const uniqueUsers = new Set(data?.map(e => e.user_id) || []);
        
        weeks.push({
          week: `Week ${4 - i}`,
          weekLabel: format(weekStart, 'MMM d'),
          activeUsers: uniqueUsers.size,
          userIds: uniqueUsers,
        });
      }

      // Calculate retention (users who returned from previous week)
      const result = weeks.map((week, index) => {
        let retentionRate = 0;
        let returningUsers = 0;

        if (index > 0 && weeks[index - 1].userIds.size > 0) {
          const previousUsers = weeks[index - 1].userIds;
          const currentUsers = week.userIds;
          
          returningUsers = [...currentUsers].filter(u => previousUsers.has(u)).length;
          retentionRate = Math.round((returningUsers / previousUsers.size) * 100);
        }

        return {
          week: week.weekLabel,
          activeUsers: week.activeUsers,
          returningUsers,
          retentionRate,
        };
      });

      return result;
    },
  });

  const avgRetention = retentionData.length > 1
    ? Math.round(
        retentionData.slice(1).reduce((sum, d) => sum + d.retentionRate, 0) / 
        (retentionData.length - 1)
      )
    : 0;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            User Retention
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[250px] flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCheck className="h-5 w-5" />
          User Retention
        </CardTitle>
        <CardDescription>
          Weekly active users and return rate • Avg retention: {avgRetention}%
        </CardDescription>
      </CardHeader>
      <CardContent>
        {retentionData.every(d => d.activeUsers === 0) ? (
          <div className="h-[250px] flex items-center justify-center text-muted-foreground">
            No user activity data available
          </div>
        ) : (
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={retentionData}>
                <XAxis 
                  dataKey="week" 
                  tick={{ fontSize: 11 }}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number, name: string) => {
                    if (name === 'activeUsers') return [value, 'Active Users'];
                    if (name === 'returningUsers') return [value, 'Returning Users'];
                    return [value, name];
                  }}
                />
                <Bar 
                  dataKey="activeUsers" 
                  fill="hsl(var(--primary))" 
                  radius={[4, 4, 0, 0]}
                  name="activeUsers"
                />
                <Bar 
                  dataKey="returningUsers" 
                  fill="hsl(var(--primary)/0.5)" 
                  radius={[4, 4, 0, 0]}
                  name="returningUsers"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
